import { Fragment, useState } from "react";
import { ChevronRight } from "lucide-react";
import type { Dre } from "@bookdental/schemas";
import { reais } from "@/lib/dinheiro";
import { cn } from "@/lib/cn";
import { categoriasDaLinha, linhasVisiveis, percentual, valorDaCategoria, type LinhaDaTabela } from "./linhas-do-dre";

export type ColunaDoDre = { chave: string; rotulo: string; dre: Dre; comPercentual?: boolean };

const cor = (v: number, l: LinhaDaTabela) => (l.forte ? (v >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400") : "text-zinc-900 dark:text-foreground");

function variacao(atual: number, antes: number) {
  if (!antes) return "—";
  const v = (atual - antes) / Math.abs(antes);
  return `${v > 0 ? "+" : ""}${(v * 100).toLocaleString("pt-BR", { maximumFractionDigits: 0 })}%`;
}

/**
 * O DRE em tabela: uma coluna por período (mês, comparação ou meses do ano). Cada linha com
 * categoria abre nelas. `variacao` = índices [antes, depois] para a coluna de variação.
 */
export function TabelaDoDre({ colunas, variacao: par, rotuloDaVariacao = "Variação" }: { colunas: ColunaDoDre[]; variacao?: [number, number]; rotuloDaVariacao?: string }) {
  const [abertas, setAbertas] = useState<Set<string>>(new Set());
  const dres = colunas.map((c) => c.dre);
  const linhas = linhasVisiveis(dres);
  const alternar = (chave: string) =>
    setAbertas((a) => {
      const n = new Set(a);
      if (n.has(chave)) n.delete(chave);
      else n.add(chave);
      return n;
    });

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-max text-sm">
        <thead>
          <tr className="border-b border-zinc-200 text-left text-[11px] font-semibold uppercase tracking-wide text-zinc-500 dark:border-border dark:text-muted-foreground">
            <th className="sticky left-0 z-10 bg-white px-4 py-2.5 dark:bg-card">Linha</th>
            {colunas.map((c) => (
              <Fragment key={c.chave}>
                <th className="px-3 py-2.5 text-right">{c.rotulo}</th>
                {c.comPercentual && <th className="px-3 py-2.5 text-right">% receita</th>}
              </Fragment>
            ))}
            {par && <th className="px-4 py-2.5 text-right">{rotuloDaVariacao}</th>}
          </tr>
        </thead>
        <tbody>
          {linhas.map((l) => {
            const cats = categoriasDaLinha(l, dres);
            const aberta = abertas.has(l.chave) && cats.length > 0;
            return (
              <Fragment key={l.chave}>
                <tr className={cn("border-b border-zinc-100 dark:border-border/50", l.total && "bg-zinc-50/70 dark:bg-muted/30")}>
                  <th scope="row" className={cn("sticky left-0 z-10 px-4 py-2 text-left font-normal", l.total ? "bg-zinc-50 font-semibold dark:bg-muted" : "bg-white dark:bg-card")}>
                    {cats.length > 0 ? (
                      <button type="button" aria-expanded={aberta} onClick={() => alternar(l.chave)} className="inline-flex items-center gap-1 text-left text-zinc-700 hover:text-zinc-900 dark:text-foreground/80 dark:hover:text-foreground">
                        <ChevronRight className={cn("h-3.5 w-3.5 shrink-0 text-zinc-400 transition-transform", aberta && "rotate-90")} />
                        {l.rotulo}
                      </button>
                    ) : (
                      <span className={cn(l.total ? "text-zinc-900 dark:text-foreground" : "pl-[18px] text-zinc-700 dark:text-foreground/80", l.forte && "text-base")}>{l.rotulo}</span>
                    )}
                    {colunas.some((c) => l.estimado?.(c.dre)) && (
                      <span title="Calculado pela alíquota até o imposto real ser lançado." className="ml-2 rounded-full bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700 dark:bg-amber-500/10 dark:text-amber-400">
                        estimado
                      </span>
                    )}
                  </th>
                  {colunas.map((c) => {
                    const v = l.valor(c.dre);
                    return (
                      <Fragment key={c.chave}>
                        <td className={cn("px-3 py-2 text-right tabular-nums", l.total && "font-semibold", l.forte && "text-base", cor(v, l))}>{reais(v)}</td>
                        {c.comPercentual && <td className="px-3 py-2 text-right tabular-nums text-zinc-500 dark:text-muted-foreground">{percentual(v, c.dre)}</td>}
                      </Fragment>
                    );
                  })}
                  {par && <td className="px-4 py-2 text-right tabular-nums text-zinc-500 dark:text-muted-foreground">{variacao(l.valor(dres[par[1]]!), l.valor(dres[par[0]]!))}</td>}
                </tr>
                {aberta &&
                  cats.map((cat) => (
                    <tr key={cat.id} className="border-b border-zinc-100 text-[13px] dark:border-border/50">
                      <th scope="row" className="sticky left-0 z-10 bg-white py-1.5 pl-10 pr-4 text-left font-normal text-zinc-500 dark:bg-card dark:text-muted-foreground">
                        <span className="mr-1.5 inline-block h-2 w-2 rounded-[2px]" style={{ backgroundColor: cat.cor || "#A1A1AA" }} />
                        {cat.nome}
                      </th>
                      {colunas.map((c) => {
                        const v = valorDaCategoria(l, c.dre, cat.id);
                        return (
                          <Fragment key={c.chave}>
                            <td className="px-3 py-1.5 text-right tabular-nums text-zinc-600 dark:text-foreground/70">{v ? reais(v) : "—"}</td>
                            {c.comPercentual && <td className="px-3 py-1.5 text-right tabular-nums text-zinc-400">{v ? percentual(v, c.dre) : ""}</td>}
                          </Fragment>
                        );
                      })}
                      {par && <td />}
                    </tr>
                  ))}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
